<template>
  <div>
    <header class="flex align-items-center justify-content-between mb-3">
      <div>
        <h2 class="m-0 p-0 font-light text-600">{{ pageTitle }}</h2>
        <p v-if="selectedEmployee" class="m-0 pt-1 text-xs text-300">
          {{ selectedEmployee.employee_name }} · {{ selectedEmployee.employee_company_name }}
        </p>
      </div>
      <Button
        label="Back"
        icon="pi pi-arrow-left"
        class="surface-300 hover:surface-500"
        @click="handleBack"
      />
    </header>

    <div v-if="isLoading" class="card text-300">
      Loading employee…
    </div>

    <div v-else-if="routeError" class="card">
      <Message severity="error" :closable="false">{{ routeError }}</Message>
    </div>

    <div v-else class="card overflow-x-auto">
      <RosterCreateDialog
        ref="rosterCreateRef"
        :selectedId="selectedEmployee"
        :shiftType="shiftType"
        :numberOfRows="1"
        @updateSuccess="handleUpdateSuccess"
        @handleBack="handleBack"
      />

      <div class="flex flex-wrap align-items-center gap-2 w-full pt-3 mt-3 border-top-1 border-300">
        <Button
          label="Cancel"
          class="surface-200 hover:surface-400"
          @click="handleBack"
        />
        <Button
          label="Generate Roster"
          severity="primary"
          :loading="rosterCreateRef?.isSubmitting"
          @click="generateRoster"
        />
        <div class="flex flex-wrap align-items-center gap-1 ml-auto">
          <span class="text-300 text-xs pr-1">Add New Rows:</span>
          <Button
            v-for="count in rowCountOptions"
            :key="count"
            :label="`+${count}`"
            :disabled="rosterCreateRef?.isMaxRowsReached || rosterCreateRef?.isSubmitting"
            class="surface-300 hover:surface-500"
            @click="handleCreateNewRows(count)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'primevue/button';
import Message from 'primevue/message';
import RosterCreateDialog from '@/components/dialogs/RosterCreateDialog.vue';
import { useEmployeesStore } from '@/stores/employees';
import {
  isRosterFilteredRoute,
  parseRosterRouteFilters
} from '@/composables/useRosterRouteFilters';

const rowCountOptions = [1, 2, 6, 13, 30, 60];
const allowedShiftTypes = new Set(['SHIFT', 'COVER']);

const route = useRoute();
const router = useRouter();
const employeesStore = useEmployeesStore();
const rosterCreateRef = ref(null);
const isInitializing = ref(true);
const loadError = ref(null);

const parsedEmployeeId = computed(() => {
  const rawEmployeeId = route.params.employeeId;
  if (typeof rawEmployeeId !== 'string' || !/^\d+$/.test(rawEmployeeId)) {
    return null;
  }

  const employeeId = Number(rawEmployeeId);
  return Number.isSafeInteger(employeeId) && employeeId > 0 ? employeeId : null;
});

const shiftType = computed(() => {
  const rawShiftType = route.params.shiftType;
  if (typeof rawShiftType !== 'string') {
    return null;
  }

  const normalizedShiftType = rawShiftType.toUpperCase();
  return allowedShiftTypes.has(normalizedShiftType) ? normalizedShiftType : null;
});

const selectedEmployee = computed(() => (
  parsedEmployeeId.value
    ? employeesStore.employeeById(parsedEmployeeId.value)
    : null
));

const isLoading = computed(() => (
  isInitializing.value || employeesStore.employeesLoading
));

const routeError = computed(() => {
  if (!parsedEmployeeId.value) {
    return 'The employee ID must be a positive integer.';
  }

  if (!shiftType.value) {
    return 'The roster type must be either shift or cover.';
  }

  if (loadError.value) {
    return loadError.value;
  }

  if (!selectedEmployee.value) {
    return `Employee ${parsedEmployeeId.value} could not be found.`;
  }

  return null;
});

const pageTitle = computed(() => (
  shiftType.value === 'COVER' ? 'Create Cover Roster' : 'Create Roster'
));

const safeReturnTo = computed(() => {
  const returnTo = route.query.returnTo;
  if (
    typeof returnTo !== 'string'
    || !returnTo.startsWith('/')
    || returnTo.startsWith('//')
  ) {
    return null;
  }

  const resolvedRoute = router.resolve(returnTo);
  if (resolvedRoute.name !== 'ViewRosterFiltered' || !isRosterFilteredRoute(resolvedRoute.name)) {
    return null;
  }

  const parsedFilters = parseRosterRouteFilters(resolvedRoute.params, 'roster');
  return parsedFilters.valid ? resolvedRoute.fullPath : null;
});

const navigateBackOrFallback = (replaceFallback = false) => {
  if (safeReturnTo.value) {
    router.replace(safeReturnTo.value);
    return;
  }

  if (window.history.state?.back) {
    router.back();
    return;
  }

  const fallbackRoute = { name: 'ViewRoster' };
  if (replaceFallback) {
    router.replace(fallbackRoute);
  } else {
    router.push(fallbackRoute);
  }
};

const handleBack = () => navigateBackOrFallback(false);
const handleUpdateSuccess = () => navigateBackOrFallback(true);
const generateRoster = () => rosterCreateRef.value?.submitForm();
const handleCreateNewRows = count => rosterCreateRef.value?.createNewRows(count);

onMounted(async () => {
  if (!parsedEmployeeId.value || !shiftType.value) {
    isInitializing.value = false;
    return;
  }

  try {
    await employeesStore.ensureEmployees();
  } catch (error) {
    loadError.value = error.response?.data?.error
      || error.message
      || 'Failed to load the employee.';
  } finally {
    isInitializing.value = false;
  }
});
</script>
